import { defaultTheme } from 'styles';
import { themes as readerBuiltInThemes } from 'lib/reader/themes';
import { editorHandlers } from './editorHandlers';
import { actions, getLanguageState } from '.';

import type { AnyAction } from 'redux';
import type { AxiosHeaders } from 'axios';
import type { LocaleMessages, ServerConfig } from 'shared-types';
import type { ReduxRootState } from 'types';

type Actions = typeof actions;
type SupportedActions = Exclude<keyof Actions, 'logout' | 'whoAmI'>;

const createReducer = (
  messages: LocaleMessages,
  serverConfig: ServerConfig,
  headers: AxiosHeaders,
  serverState: ReduxRootState
) => {
  const language = getLanguageState({ serverConfig, headers, serverState });

  const initialState: ReduxRootState = {
    accountDeleteRequests: 0,
    language: language,
    messages: {
      ...messages,
      ...(serverConfig?.customAdminLocales?.[language] || {})
    },
    notices: [] as ReduxRootState['notices'],
    pageState: {} as ReduxRootState['pageState'],
    pendingJobs: { initialized: false, jobs: {} },
    serverConfig: serverConfig,
    supervisorUnreviewedItems: 0,
    theme: {
      ...defaultTheme,
      ...(serverConfig ? serverConfig.customTheme : {})
    } as ReduxRootState['theme'],
    user: null
  };

  const actions: Record<
    SupportedActions,
    (state: ReduxRootState, action: AnyAction) => ReduxRootState
  > = {
    addNotice: (state, action) => ({
      ...state,
      notices: [...state.notices, action.data]
    }),
    dismissNotice: (state, action) => ({
      ...state,
      notices: [...state.notices.filter((data) => data.id !== action.id)]
    }),
    registerPendingJob: (state, action) => ({
      ...state,
      pendingJobs: {
        ...state.pendingJobs,
        jobs: {
          ...state.pendingJobs.jobs,
          [action.data.jobId]: {
            messages: action.data.messages,
            type: action.data.type
          }
        }
      }
    }),
    removePendingJob: (state, action) => {
      const { [action.jobId]: _removed, ...remaining } = state.pendingJobs.jobs;
      return {
        ...state,
        pendingJobs: {
          ...state.pendingJobs,
          jobs: remaining
        }
      };
    },
    resetPendingJobs: (state) => ({
      ...state,
      pendingJobs: {
        initialized: false,
        jobs: {}
      }
    }),
    setAccountDeleteRequests: (state, action) => ({
      ...state,
      accountDeleteRequests: action.accountDeleteRequests
    }),
    setInitialPageState: (state, action) => ({
      ...state,
      pageState: {
        ...action.pageState
      }
    }),
    setLanguage: (state, action) => ({
      ...state,
      language: action.language,
      messages: action.messages
    }),
    setPendingJobs: (state, action) => ({
      ...state,
      pendingJobs: {
        initialized: true,
        jobs: action.jobs
      }
    }),
    setServerConfig: (state, action) => ({
      ...state,
      serverConfig: {
        ...action.serverConfig
      }
    }),
    setSupervisorUnreviewedItems: (state, action) => ({
      ...state,
      supervisorUnreviewedItems: action.supervisorUnreviewedItems
    }),
    setUser: (state, action) => ({
      ...state,
      user: action.user
    }),
    updateEditorPageState: (state, action) => ({
      ...state,
      pageState: {
        ...state.pageState,
        initialStatePath: null,
        ...(editorHandlers.hasOwnProperty(action.data.type)
          ? editorHandlers[action.data.type]({
              pageState: state.pageState,
              data: action.data
            })
          : {})
      }
    }),
    updatePageState: (state, action) => ({
      ...state,
      pageState: {
        ...state.pageState,
        initialStatePath: null,
        ...action.pageState
      }
    }),
    updateReaderTheme: (state, action) => ({
      ...state,
      serverConfig: {
        ...state.serverConfig,
        readerCustomTheme: action.data,
        readerCustomThemeId: action.id
      },
      ...(state.pageState.readerTheme
        ? {
            pageState: {
              ...state.pageState,
              readerTheme: action.id
                ? { ...readerBuiltInThemes.blue, ...action.data }
                : readerBuiltInThemes[state.serverConfig.readerDefaultTheme] ||
                  readerBuiltInThemes.blue
            }
          }
        : {})
    }),
    updateTheme: (state, action) => ({
      ...state,
      serverConfig: {
        ...state.serverConfig,
        customTheme: action.data,
        customThemeId: action.id
      },
      theme: {
        ...defaultTheme,
        ...action.data
      }
    })
  };

  return (state = initialState, action: AnyAction) => {
    if (actions.hasOwnProperty(action.type)) {
      return actions[action.type](state, action);
    }
    return state;
  };
};

export default createReducer;
